import _ = require("underscore");
import $ = require("jquery");
import Utils = require("../utils");
import Portal = require("portal-gis");
import globals = require("../../globals");
import JSV = require("../utils/jsv");
import View = require("./base/geoquery-form-tool");
import Services = require("../services/services");

const validTypes = {
    "datetime": "date",
    "string": "string",
    "int": "number",
    "Decimal": "number",
    "Double": "number"
};

interface QueryOperator {
    operator: string;
    property?: string;
    value?: string;
    criteria?: Array<QueryOperator>;
}

class Tool extends View {

    static about = `
Create a query form for an IPS service.

Usage Example:

    <CONTROL disabled="False" id="cdr-application-filter" mid="app/controls/ips-geoquery-form-tool" text="CDR Application" type="execute">
    <OPTIONS>
        <Values id="service" value="resource/cdr" />
        <Values id="service-model" value="Application" />
        <Values id="query-service" value="cdr/applications/planning" />
        <Values id="srs" value="EPSG:3421" />
        <Values id="symbology" value="Hansen.CDR.Planning.Inspection" />
        <Values id="zoommap" value="false" />
        <Values about="Only these fields may appear on the form and only if they are enabled and not blacklisted" id="whitelist" value="ApplicationDateTime,ApplicationName,Comments,ExpireDateTime,Status,TotalUnpaidFees,Type,WorkType" />
        <Values about="These fields will not show on this form" id="blacklist" value="DeclaredValuation,LastModifiedDateTime" />
    </OPTIONS>
    <COMMANDS>
        <COMMAND id="geoquery-form-search" mid="app/commands/trigger" text="Search" type="action">
        <OPTIONS>
            <Values id="event" value="ips-geoquery-execute" />
        </OPTIONS>
        </COMMAND>
    </COMMANDS>
    <CONTROLS>
        <CONTROL about="Replaces the default input control with a date range control" id="ExpireDateTime" mid="app/test/controls/daterange" text="Expiration Date">
        <OPTIONS>
            <Values id="default-lo" value="-14" />
            <Values id="default-hi" value="14" />
        </OPTIONS>
        </CONTROL>
        <CONTROL about="Replaces the default input control with a fancy-pants editor" id="Comments" mid="app/test/controls/editor" text="Comments?">
        <OPTIONS>
            <Values id="default-value" value="[hello]" />
        </OPTIONS>
        </CONTROL>
        <CONTROL about="Display checkboxes or radio buttons instead of the default input control for selecting one or more status values" id="Status" mid="app/test/controls/domain" text="Status Filter">
        <OPTIONS>
            <Values about="Could add Attributes to commands, could introduce a &quot;model&quot; option, could dedicate options to the domain values, could do delimited value on &quot;domain&quot; but this covers the need of non-numeric domain keys and and allows for other options to be provided." id="domain" value="{&#xD;&#xA;    &quot;Open&quot;: &quot;open&quot;,&#xD;&#xA;    &quot;Closed&quot;: &quot;closed&quot;,&#xD;&#xA;    &quot;Inactive&quot;: &quot;0&quot;,&#xD;&#xA;    &quot;Active&quot;: 1&#xD;&#xA;}" />
            <Values about="show checkboxes instead of radio buttons" id="multi-select" value="true" />
            <Values id="domain-default" value="open,closed" />
        </OPTIONS>
        </CONTROL>
        <CONTROL about="Display checkboxes or radio buttons instead of the default input control for selecting one or more status values" id="WorkType" mid="app/test/controls/domain" text="Work Type">
        <OPTIONS>
            <Values about="Valid WorkType values for a CDR application" id="domain" value="{&#xD;&#xA;    &quot;Open&quot;: &quot;open&quot;,&#xD;&#xA;    &quot;Closed&quot;: &quot;closed&quot;&#xD;&#xA;}" />
            <Values about="show checkboxes instead of radio buttons" id="multi-select" value="false" />
        </OPTIONS>
        </CONTROL>
        <CONTROL about="Disable this field as if it were blacklisted or just disable this override?" disabled="True" id="IssuedDateTime" mid="app/test/controls/daterange" text="Date Issued" />
        <CONTROL about="Relabel this field" disabled="False" id="ProcessState" mid="app/test/controls/editor" text="Process State" />
    </CONTROLS>
    </CONTROL>

    `;

    static options = {
        "service": "The IPS service endpoint (e.g. resource/cdr)",
        "service-model": "The IPS service model to query (e.g. Application)",
    }

    // creates the metadata then uses the base form builder
    createForm(metadata?: any) {

        if (metadata) {
            super.createForm(metadata);
            return;
        }

        let options = this.options;

        Utils.ifOption(options, "service", service => {

            Utils.ifOption(options, "service-model", modelId => {

                let metaService = new Services.MetaService(null, service);

                metaService.fetch().done(serviceInfo => {
                    let model = serviceInfo.models[modelId];
                    if (!model) throw `unknown model: ${modelId}`;

                    let props = model.properties;
                    let keys = _.keys(props).filter(key => validTypes[props[key].type]);

                    metadata = keys.map(key => ({
                        name: key,
                        type: validTypes[props[key].type],
                        alias: key,
                        nullable: !(props[key].required)
                    }));

                    super.createForm({
                        metadata: metadata
                    });

                });
            }, () => console.warn("service-model not provided"));
        }, () => console.warn("service not provided"));

    }

    asQuery(data: Array<{ name: string; value: any; }>): QueryOperator {
        let query = data.map(item => {
            let property = item.name;
            if (item.value.range) {
                let range = <{ lo: any; hi: any }>item.value.range;
                if (range.lo && !range.hi) {
                    return <QueryOperator>{
                        property: property,
                        operator: "GreaterThanOrEqual",
                        value: range.lo
                    }
                } else if (!range.lo && range.hi) {
                    return <QueryOperator>{
                        property: property,
                        operator: "LessThan",
                        value: range.hi
                    }
                } else {
                    return <QueryOperator>{
                        operator: "And",
                        criteria: [
                            {
                                property: property,
                                operator: "GreaterThanOrEqual",
                                value: range.lo
                            },
                            {
                                property: property,
                                operator: "LessThan",
                                value: range.hi
                            }]
                    };
                }
            }
            else if (_.isArray(item.value)) {
                return <QueryOperator>{
                    operator: "Or",
                    criteria: item.value.map(v => (
                        {
                            property: property,
                            operator: "Equals",
                            value: v
                        }))
                };
            }
            else
                return <QueryOperator>{
                    property: property,
                    operator: "Equals",
                    value: item.value
                };
        });

        return query.length === 0 ? null : { operator: "And", criteria: query };

    }

}

export = Tool; 